import React, { useState, useMemo } from "react";
import { GarmentMeasurementResult } from "../../../utilities/types/free-trial/garment-measurement-service/types";
import UnitToggle from "../UnitToggle";

interface GarmentMeasurementResultDisplayProps {
  data: GarmentMeasurementResult;
  uploadedImage?: string;
}

const CM_TO_INCH = 0.393701;

const formatLabel = (key: string): string => {
  if (!key) return "";
  const formatted = key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const GarmentMeasurementResultDisplay: React.FC<GarmentMeasurementResultDisplayProps> = ({ data, uploadedImage }) => {
  const [measurementUnit, setMeasurementUnit] = useState<"cm" | "inch">("cm");

  const measurementItems = useMemo(() => {
    if (!data) return [];

    const items = [];
    for (const key in data) {
      if (key === "Details" || key === "MetaData") continue;
      const value = data[key];
      if (value === undefined || value === 0) continue;
      const label = formatLabel(key);
      items.push({ key, label, value: measurementUnit === "inch" ? +(value * CM_TO_INCH).toFixed(2) : +value.toFixed(2) });
    }

    return items.sort((a, b) => {
      if (a.key.includes("Top") && !b.key.includes("Top")) return -1;
      if (!a.key.includes("Top") && b.key.includes("Top")) return 1;
      if (a.key.includes("Bottom") && !b.key.includes("Bottom")) return 1;
      if (!a.key.includes("Bottom") && b.key.includes("Bottom")) return -1;
      return 0;
    });
  }, [data, measurementUnit]);

  const hasMeasurements = measurementItems.length > 0;

  return (
    <div className="flex flex-col items-center w-full gap-8">
      <div className="flex flex-col items-center w-full max-w-4xl gap-6 md:items-start md:flex-row">
        {uploadedImage && (
          <div className="grid place-content-center w-[15rem] h-[25rem] border-2 rounded-md border-gorgonzola-blue-50 overflow-hidden flex-shrink-0">
            <img src={`data:image/png;base64,${uploadedImage}`} alt="Uploaded" className="object-contain w-[15rem] h-[25rem]" />
          </div>
        )}

        <div className="flex flex-col items-center flex-1 w-full gap-4">
          <div className="flex justify-center w-full">
            <UnitToggle selectedUnit={measurementUnit} onUnitChange={setMeasurementUnit} />
          </div>{" "}
          {!hasMeasurements ? (
            <div className="py-8 text-center">
              <p className="text-gorgonzola-blue-400">No measurements available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 overflow-y-auto max-h-[30rem] custom_scroll w-full">
              {measurementItems.map((item, index) => (
                <div key={index} className="flex items-center justify-between gap-4 p-4 border rounded-lg border-gorgonzola-blue-50">
                  <span className="text-sm font-medium text-gorgonzola-blue-800">{item.label}</span>
                  <div className="flex items-end">
                    <span className="font-bold text-gorgonzola-blue-600">{item.value}</span>
                    <span className="ml-1 text-xs text-gorgonzola-blue-400">{measurementUnit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GarmentMeasurementResultDisplay;
